import React, { useState, useRef, useEffect } from 'react';

interface TailwindMultiSelectProps {
  options: { id: string; text: string; disabled?: boolean }[];
  value: any[];
  onChange: (value: any[]) => void;
  placeholder?: string;
  clearable?: boolean;
  searchable?: boolean;
  className?: string;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
  onOpenDropDown?: () => void;
}

const TailwindMultiSelect: React.FC<TailwindMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  clearable = true,
  searchable = true,
  className,
  disabled,
  onBlur,
  onFocus,
  onOpenDropDown
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasOpenedRef = useRef(false);

  const filteredOptions = searchable ? options.filter(option => option.text.toLowerCase().includes(search.toLowerCase())) : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        hasOpenedRef.current = false;
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = () => {
    if (!disabled) {
      if (!isOpen && !hasOpenedRef.current) {
        onOpenDropDown?.();
        hasOpenedRef.current = true;
      }
      setIsOpen(true);
    }
  };

  const handleSelect = (option: { id: string; text: string; disabled?: boolean }) => {
    if (option.disabled) return;

    const newValue = value.find(v => v.id === option.id) ? value.filter(v => v.id !== option.id) : [...value, option];
    onChange(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
    setSearch('');
  };

  return (
    <div className={`disabled relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`} ref={dropdownRef}>
      <div
        className={`min-h-[38px] p-2 border rounded-md bg-white cursor-pointer flex flex-wrap gap-2 ${
          isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
        } ${className || ''}`}
        onClick={handleOpen}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {value.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {value.map(item => (
              <span key={item.id} className="bg-gray-100 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                {item.text}
                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleSelect(item);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">{placeholder}</span>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {searchable && (
            <input
              type="text"
              className="w-full p-2 border-b border-gray-300 focus:outline-none"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          )}
          {filteredOptions.map(option => (
            <div
              key={option.id}
              className={`p-2 cursor-pointer ${
                option.disabled
                  ? 'cursor-default text-gray-600'
                  : `hover:bg-gray-400 ${value?.find(v => v.id === option.id) ? 'bg-blue-50 text-black-700' : ''}`
              }`}
              onClick={() => !option.disabled && handleSelect(option)}
            >
              {option.text}
            </div>
          ))}
          {filteredOptions.length === 0 && <div className="p-2 text-gray-600 text-center">No options found</div>}
        </div>
      )}

      {clearable && value.length > 0 && (
        <button onClick={handleClear} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
          ×
        </button>
      )}
    </div>
  );
};

export default TailwindMultiSelect;
